import {useCallback, useEffect, useState} from 'react'
import {ChevronDown, ChevronUp, RefreshCw} from 'lucide-react'
import {Text} from '@/components/tailwind/text'
import {Badge} from '@/components/tailwind/badge'
import {useMembersTranslation} from "@/i18n/hooks"
import {useAuth} from '@/context/use-auth'
import LoadingSpinner from "@/components/loader-spinner"
import {getMemberAuditLogs} from '@/services/admin-audit-log-services'
import type {AuditLogEntry} from '@/services/admin-audit-log-services'
import {formatDateString, formatTimeString} from '@/lib/utils'
import {Button} from '@/components/tailwind/button'
import {Card} from '@/components/ui/card'
import log from '@/lib/logger'

const PAGE_SIZE = 10

interface MemberAuditLogSectionProps {
    memberId: string
    refreshTrigger?: number
}

function formatDetails(details: string | null, t: (key: string, options?: Record<string, unknown>) => string): string {
    if (!details) return '-'
    try {
        const parsed = JSON.parse(details)

        if (parsed.changedFields) {
            const fieldLabels = (parsed.changedFields as string[]).map(
                (field: string) => t(`memberAuditLog.fieldName.${field}`)
            )
            return fieldLabels.join(', ')
        }

        if (parsed.type) {
            const typeLabel = t(`memberAuditLog.detailType.${parsed.type}`)

            if (parsed.type === "resignationPointsInherited") {
                return t("memberAuditLog.inherited", {name: parsed.sourceMemberName, amount: parsed.amount})
            }

            if (parsed.consentType) {
                const consentLabel = t(`memberAuditLog.consentType.${parsed.consentType}`)
                const actionLabel = t(`memberAuditLog.detailAction.${parsed.action}`)
                return `${typeLabel}: ${consentLabel} ${actionLabel}`
            }

            if (parsed.action) {
                const actionLabel = t(`memberAuditLog.detailAction.${parsed.action}`)
                return `${typeLabel} ${actionLabel}`
            }

            if (parsed.feeTypeName) {
                return `${typeLabel}: ${parsed.feeTypeName}`
            }

            if (parsed.cancellationDate) {
                return `${typeLabel}: ${parsed.cancellationDate}`
            }

            if (parsed.outcome) {
                const outcomeLabel = t(`memberAuditLog.settlementOutcome.${parsed.outcome}`)
                return parsed.amount
                    ? `${typeLabel}: ${outcomeLabel} (${parsed.amount})`
                    : `${typeLabel}: ${outcomeLabel}`
            }

            return typeLabel
        }

        if (parsed.addedServices || parsed.removedServices || parsed.isDriverChanged) {
            const parts: string[] = []
            if (parsed.addedServices?.length > 0) {
                const services = (parsed.addedServices as string[]).join(', ')
                parts.push(t('memberAuditLog.services.added', {services}))
            }
            if (parsed.removedServices?.length > 0) {
                const services = (parsed.removedServices as string[]).join(', ')
                parts.push(t('memberAuditLog.services.removed', {services}))
            }
            if (parsed.isDriverChanged) {
                parts.push(t('memberAuditLog.services.driverChanged'))
            }
            return parts.join('; ')
        }

        if (parsed.oldStatus && parsed.newStatus) {
            const oldLabel = t(`memberAuditLog.statusValue.${parsed.oldStatus}`)
            const newLabel = t(`memberAuditLog.statusValue.${parsed.newStatus}`)
            return t('memberAuditLog.statusChange', {from: oldLabel, to: newLabel})
        }

        return details
    } catch {
        return details
    }
}

function getActionBadgeColor(action: string): "green" | "blue" | "red" | "zinc" {
    switch (action) {
        case 'CREATED':
            return 'green'
        case 'UPDATED':
            return 'blue'
        case 'SOFT_DELETED':
            return 'red'
        default:
            return 'zinc'
    }
}

export function MemberAuditLogSection({
    memberId,
    refreshTrigger
}: MemberAuditLogSectionProps) {
    const {t} = useMembersTranslation()
    const {token} = useAuth()

    const [isExpanded, setIsExpanded] = useState(false)
    const [loading, setLoading] = useState(false)
    const [entries, setEntries] = useState<AuditLogEntry[]>([])
    const [currentPage, setCurrentPage] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [totalElements, setTotalElements] = useState(0)

    const fetchAuditLogs = useCallback((page: number = 0) => {
        if (!token || !memberId) return
        setLoading(true)
        getMemberAuditLogs(token, memberId, {page, size: PAGE_SIZE})
            .then((result) => {
                setEntries(result.entries)
                setCurrentPage(result.page)
                setTotalPages(result.totalPages)
                setTotalElements(result.totalElements)
            })
            .catch((err) => {
                log.error('Failed to load member audit logs:', err)
                setEntries([])
            })
            .finally(() => {
                setLoading(false)
            })
    }, [token, memberId])

    useEffect(() => {
        if (isExpanded) {
            fetchAuditLogs(0)
        }
    }, [isExpanded, fetchAuditLogs])

    useEffect(() => {
        if (isExpanded && refreshTrigger !== undefined) {
            fetchAuditLogs(0)
        }
    }, [refreshTrigger, isExpanded, fetchAuditLogs])

    const handleToggle = () => {
        setIsExpanded(!isExpanded)
    }

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            fetchAuditLogs(currentPage - 1)
        }
    }

    const handleNextPage = () => {
        if (currentPage < totalPages - 1) {
            fetchAuditLogs(currentPage + 1)
        }
    }

    return (
        <Card>
            <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                        {t('memberAuditLog.title')}
                    </div>
                    <button
                        onClick={() => fetchAuditLogs(currentPage)}
                        disabled={loading}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                        title={t('memberAuditLog.refresh')}
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                <Button
                    plain
                    onClick={handleToggle}
                    className="flex items-center gap-2 text-sm font-medium p-0 h-auto hover:bg-transparent"
                    data-testid="audit-logs-toggle"
                >
                    {isExpanded ? (
                        <>
                            <ChevronUp className="h-4 w-4"/>
                            <Text>{t('memberAuditLog.hide')}</Text>
                        </>
                    ) : (
                        <>
                            <ChevronDown className="h-4 w-4"/>
                            <Text>{t('memberAuditLog.show')}</Text>
                            {totalElements > 0 && (
                                <Badge color="zinc" className="ml-1">{totalElements}</Badge>
                            )}
                        </>
                    )}
                </Button>

                {isExpanded && (
                    <div className="mt-4 space-y-4" data-testid="audit-logs-list">
                        {loading ? (
                            <LoadingSpinner />
                        ) : entries.length === 0 ? (
                            <Text className="text-zinc-500 dark:text-zinc-400">
                                {t('memberAuditLog.noEntries')}
                            </Text>
                        ) : (
                            <>
                                {entries.map((entry) => (
                                    <div key={entry.id} className="flex gap-3 py-2">
                                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${getActionDotColor(entry.action)}`}></div>
                                        <div className="flex-1 space-y-1">
                                            <div className="flex items-center gap-2">
                                                <Badge color={getActionBadgeColor(entry.action)}>
                                                    {t(`memberAuditLog.action.${entry.action}`)}
                                                </Badge>
                                                <Text className="text-xs text-gray-500">
                                                    {formatDateString(entry.createdAt)} {formatTimeString(entry.createdAt)}
                                                </Text>
                                            </div>
                                            {entry.details && (
                                                <Text className="text-sm text-gray-600 dark:text-gray-400">
                                                    {formatDetails(entry.details, t)}
                                                </Text>
                                            )}
                                            <Text className="text-xs text-gray-500">
                                                {t('memberAuditLog.by')} {entry.createdByName || '-'}
                                            </Text>
                                        </div>
                                    </div>
                                ))}

                                {totalPages > 1 && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                                        <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                                            {t('memberAuditLog.pagination.page', {
                                                current: currentPage + 1,
                                                total: totalPages,
                                            })}
                                        </Text>
                                        <div className="flex gap-2">
                                            <Button
                                                plain
                                                disabled={currentPage === 0}
                                                onClick={handlePreviousPage}
                                            >
                                                {t('memberAuditLog.pagination.previous')}
                                            </Button>
                                            <Button
                                                plain
                                                disabled={currentPage >= totalPages - 1}
                                                onClick={handleNextPage}
                                            >
                                                {t('memberAuditLog.pagination.next')}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </Card>
    )
}

function getActionDotColor(action: string): string {
    switch (action) {
        case 'CREATED':
            return 'bg-green-500'
        case 'UPDATED':
            return 'bg-blue-500'
        case 'SOFT_DELETED':
            return 'bg-red-500'
        default:
            return 'bg-zinc-500'
    }
}
